// Shared helpers for the Scan PDF screen.
//
// Two services are involved:
//   * the dropdown service behind the Generate with Aulas AI form. Scan PDF reads the
//     same pattern / class / subject / chapter lists from it, so both screens always
//     offer identical choices.
//   * the PDF parser service, which turns an uploaded paper into questions.

import envConfig from 'env.json';

export const DROPDOWN_BASE = 'https://aitestnew.aulas.in';

// Where the PDF parser service lives, from the first of these that is set:
//   1. REACT_APP_PDF_PARSER_URL, an explicit override at build or start time (handy for local testing).
//   2. PDF_PARSER_URL in the environment file (envs/<environment>.json, copied to src/env.json by
//      `npm run env:<environment>`), like every other service address in this app.
//   3. Development only: a parser running on this machine.
// A production build with none of them shows a clear message instead of calling localhost.
export const resolveParserBase = ({ explicit, configured, isProduction }) => {
  const chosen = explicit !== undefined ? explicit : configured || undefined;
  const value = chosen !== undefined ? chosen : isProduction ? '' : 'http://localhost:8000';
  return String(value).replace(/\/+$/, '');
};

export const PARSER_BASE = resolveParserBase({
  explicit: process.env.REACT_APP_PDF_PARSER_URL,
  configured: envConfig && envConfig.PDF_PARSER_URL,
  isProduction: process.env.NODE_ENV === 'production'
});

export const POLL_INTERVAL_MS = 1500;
export const MAX_PDF_MB = 50;
export const MAX_PDFS = 6; // one test can be built from up to this many PDFs (for example one per subject)

// Figure addresses come back relative ("/v1/figures/..."); the image is fetched
// straight from the parser, the same way Aulas AI diagrams come from their host.
export const figureSrc = (figure) => {
  if (!figure || !figure.url) return null;
  return /^https?:\/\//i.test(figure.url) ? figure.url : `${PARSER_BASE}${figure.url}`;
};

// FastAPI reports validation failures as {detail: [{loc, msg}, ...]} and most
// other errors as {detail: "..."}; flatten either into one readable line.
export const formatDetail = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const loc = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
        return loc ? `${loc}: ${item.msg}` : item.msg || JSON.stringify(item);
      })
      .join('; ');
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return '';
};

// ---- dropdown shaping: same rules as TestGenerationTab ----------------------

// Dropdown endpoints return [{ id, name }]. The id is the value the API expects
// back for pattern / class / subject, the name is what a person should read.
export const toOptions = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw && raw.data) ? raw.data : [];
  return list
    .map((item) => {
      if (item === null || item === undefined) return null;
      if (typeof item === 'object') {
        const value = item.id ?? item.value ?? item.name;
        if (value === null || value === undefined) return null;
        return { value: String(value), label: String(item.name ?? item.label ?? value) };
      }
      return { value: String(item), label: String(item) };
    })
    .filter(Boolean);
};

// Chapters are keyed by NAME, not id, and class=Both returns both classes with the
// order restarting in each, so group by class and drop repeated names.
export const toChapterOptions = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw && raw.data) ? raw.data : [];
  return list
    .map((item, index) => {
      if (item === null || item === undefined) return null;
      if (typeof item === 'object') {
        const name = item.name ?? item.id;
        if (name === null || name === undefined) return null;
        return {
          value: String(name),
          label: String(name),
          key: String(item.id ?? name),
          class_: item.class_ ?? item.class ?? '',
          order: typeof item.order === 'number' ? item.order : index
        };
      }
      return { value: String(item), label: String(item), key: String(item), class_: '', order: index };
    })
    .filter(Boolean)
    .sort((a, b) => String(a.class_).localeCompare(String(b.class_)) || a.order - b.order)
    .filter((chapter, index, all) => all.findIndex((other) => other.value === chapter.value) === index);
};

// ---- parser service calls ---------------------------------------------------

const request = async (path, options) => {
  let response;
  try {
    response = await fetch(`${PARSER_BASE}${path}`, options);
  } catch (err) {
    throw new Error('The scan service could not be reached. Check that it is running and try again.');
  }
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = formatDetail(body.detail ?? body.message ?? body);
    } catch (err) {
      // not JSON; fall through to the status text
    }
    throw new Error(detail || `The scan service answered ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const parserConfigured = () => PARSER_BASE !== '';

export const checkParser = async () => {
  const response = await fetch(`${PARSER_BASE}/healthz`);
  if (!response.ok) throw new Error(String(response.status));
  return response.json();
};

// The app scrolls inside its own content area, not the page, so a dropdown left on the page (antd's default)
// stays put while its box scrolls away. Opening it inside the box's own parent keeps the two together.
export const popupInside = (trigger) => (trigger && trigger.parentNode) || document.body;

// `context` says what the PDF is about ({ subject, subjects, chapters: { subject: [names] } }), so the service
// can label each question with its subject and a chapter from the institute's own list.
// `generateAnswers` asks the service to solve the questions the paper prints no answer for (it costs more tokens).
export const uploadPdf = (file, force = false, context = null, generateAnswers = false) => {
  const body = new FormData();
  body.append('file', file);
  if (context) body.append('context', JSON.stringify(context));
  if (generateAnswers) body.append('generate_answers', 'true');
  return request(`/v1/documents${force ? '?force=true' : ''}`, { method: 'POST', body });
};

export const getJob = (jobId) => request(`/v1/jobs/${encodeURIComponent(jobId)}`);

export const getQuestions = async (jobId) => {
  const all = [];
  for (let offset = 0; ; offset += 100) {
    const page = await request(`/v1/jobs/${encodeURIComponent(jobId)}/questions?offset=${offset}&limit=100`);
    all.push(...page);
    if (page.length < 100) break;
  }
  return all;
};

export const patchQuestion = (questionId, body) =>
  request(`/v1/questions/${encodeURIComponent(questionId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
